import { useState } from 'react';
import {
  Box, TextField, InputAdornment, IconButton, CircularProgress, Stack, Chip, Button, Typography, Alert,
} from '@mui/material';
import { Search, CheckCircle, Verified, Download } from '@mui/icons-material';
import { gstApi, type GstDetails } from '../api/gst.api';
import { extractApiError } from '../api/error';

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

interface Props {
  label?: string;
  value?: string | null;
  onChange: (v: string) => void;
  /** Fired with fetched details so the parent can auto-fill (e.g. company/legal name). */
  onDetails?: (d: GstDetails) => void;
  fullWidth?: boolean;
}

/**
 * Reusable GSTIN input: live format check, a Verify action that validates (format + checksum,
 * server-side) and derives state/PAN/holder-type (plus provider details when configured), and a
 * one-click PDF summary — handy when a dealer/vendor/client gives only the GST number.
 */
export function GstinField({ label = 'GSTIN', value, onChange, onDetails, fullWidth = true }: Props) {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<GstDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const gstin = (value ?? '').toUpperCase();
  const formatOk = GSTIN_RE.test(gstin);
  const verified = details?.valid && details.gstin === gstin;

  const verify = async () => {
    if (gstin.length !== 15) { setError('GSTIN must be 15 characters'); return; }
    setLoading(true); setError(null); setDetails(null);
    try {
      const d = await gstApi.lookup(gstin);
      setDetails(d);
      if (d.valid) { setError(null); onDetails?.(d); } else setError(d.error ?? 'Invalid GSTIN');
    } catch (e) {
      setError(extractApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try { await gstApi.downloadSummary(gstin); } catch (e) { setError(extractApiError(e)); } finally { setDownloading(false); }
  };

  return (
    <Box>
      <TextField
        label={label} size="small" fullWidth={fullWidth} value={gstin}
        onChange={(e) => { onChange(e.target.value.toUpperCase()); if (details || error) { setDetails(null); setError(null); } }}
        onKeyDown={(e) => { if (e.key === 'Enter' && formatOk) { e.preventDefault(); void verify(); } }}
        error={Boolean(error)}
        helperText={error || (verified ? 'Verified' : 'Enter 15-char GSTIN, then Verify to fetch bank/branch-style details')}
        InputProps={{ endAdornment: (
          <InputAdornment position="end">
            {loading
              ? <CircularProgress size={16} />
              : verified
                ? <CheckCircle color="success" fontSize="small" />
                : <IconButton size="small" edge="end" title="Verify GSTIN" disabled={!formatOk} onClick={() => void verify()}><Search fontSize="small" /></IconButton>}
          </InputAdornment>
        ) }}
      />

      {details?.valid && (
        <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" icon={<Verified />} color={details.verified ? 'success' : 'info'}
              label={details.verified ? 'Verified by provider' : 'Format & checksum valid'} />
            {details.state && <Chip size="small" variant="outlined" label={details.state} />}
            {details.holderType && <Chip size="small" variant="outlined" label={details.holderType} />}
          </Stack>
          <Stack spacing={0.25}>
            {details.legalName && <Kv k="Legal name" v={details.legalName} />}
            {details.tradeName && <Kv k="Trade name" v={details.tradeName} />}
            <Kv k="PAN" v={details.pan} />
            {details.status && <Kv k="Status" v={details.status} />}
            {details.address && <Kv k="Address" v={details.address} />}
            {!details.verified && (
              <Typography variant="caption" color="text.secondary">
                Live registration details (legal name, address, status) appear here once a GST verification provider is configured.
              </Typography>
            )}
          </Stack>
          <Button size="small" startIcon={<Download />} sx={{ mt: 1 }} disabled={downloading} onClick={() => void download()}>
            {downloading ? 'Preparing…' : 'Download details PDF'}
          </Button>
        </Box>
      )}
      {error && !details && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{error}</Alert>}
    </Box>
  );
}

const Kv = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <Stack direction="row" spacing={1}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>{k}</Typography>
    <Typography variant="caption" fontWeight={600}>{v ?? '—'}</Typography>
  </Stack>
);
